import React, { useState, useRef } from 'react';
import { Upload, X, RefreshCw, ImageIcon, Camera, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_EXT = 'JPEG, PNG, WEBP';
const MAX_MB = 2;

/**
 * UploadCard — premium drag & drop image upload component for registration forms
 * Preserves the same logic as ImageUpload.js:
 * - Stores File object via onChange (for later upload on form submit)
 * - Shows local preview (base64)
 * - If entityId + onUpload provided, uploads immediately
 */
const UploadCard = ({
  value,
  onChange,
  onUpload = null,
  entityType = 'school',
  entityId = null,
  label = 'Muat Naik Imej',
  required = false,
  hint = '',
  className = '',
}) => {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const isMember = entityType === 'member';
  const inputId = `upload-card-${entityType}-${Math.random().toString(36).slice(2, 6)}`;

  const handleFileSelect = async (file) => {
    if (!file) return;
    setError('');

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(`Jenis fail tidak dibenarkan. Gunakan ${ALLOWED_EXT}.`);
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`Saiz fail melebihi had ${MAX_MB}MB.`);
      return;
    }

    // Local preview
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);

    // If immediate upload (for admin edit flows)
    if (entityId && onUpload) {
      setUploading(true);
      try {
        const url = await onUpload(file);
        onChange(url);
        toast.success('Imej berjaya dimuat naik');
      } catch (err) {
        setError(err.message || 'Gagal memuat naik imej');
        setPreview(value || null);
      } finally {
        setUploading(false);
      }
    } else {
      // Store file for later upload on form submit
      onChange(file);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setError('');
    onChange(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleChange = (e) => {
    if (e.target.files?.[0]) handleFileSelect(e.target.files[0]);
  };

  // Resolve display URL
  const displayUrl = preview?.startsWith('data:') || preview?.startsWith('blob:')
    ? preview
    : preview
    ? `${API_URL}${preview}`
    : value && typeof value === 'string'
    ? `${API_URL}${value}`
    : null;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-xs font-bold text-[#0B0B0B] uppercase tracking-wide">
          {label}
          {required && <span className="text-[#D71920] ml-1">*</span>}
        </label>
      )}

      {/* Upload area */}
      {displayUrl ? (
        /* Preview State */
        <div className="border border-[#E5E5E5] rounded bg-[#F7F7F7] p-5">
          <div className="flex items-center gap-5">
            {/* Image preview */}
            <div className="flex-shrink-0 relative">
              <img
                src={displayUrl}
                alt="Preview"
                className={`object-cover border border-[#E5E5E5] ${
                  isMember
                    ? 'w-20 h-20 rounded-full'
                    : 'w-28 h-20 rounded'
                }`}
              />
              {uploading && (
                <div className="absolute inset-0 bg-black/50 rounded flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-white animate-spin" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0B0B0B] mb-1">
                {isMember ? 'Foto dipilih' : 'Logo dipilih'}
              </p>
              <p className="text-xs text-[#777] mb-3">
                Pratonton imej dipaparkan. Imej akan dimuat naik semasa penghantaran.
              </p>
              <div className="flex items-center gap-2">
                <label
                  htmlFor={inputId}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0B0B0B] border border-[#E5E5E5] bg-white hover:bg-[#F7F7F7] px-3 py-1.5 rounded cursor-pointer transition-colors"
                >
                  <RefreshCw size={11} /> Tukar
                </label>
                <button
                  type="button"
                  onClick={handleRemove}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#D71920] border border-[#D71920]/30 bg-[#FEF2F2] hover:bg-[#D71920]/10 px-3 py-1.5 rounded cursor-pointer transition-colors"
                >
                  <X size={11} /> Buang
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Upload State */
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded transition-all duration-200 ${
            dragActive
              ? 'border-[#0B0B0B] bg-[#F7F7F7] scale-[1.01]'
              : error
              ? 'border-[#D71920]/50 bg-[#FEF2F2]'
              : 'border-[#E5E5E5] hover:border-[#0B0B0B]/40 bg-white'
          }`}
        >
          <label
            htmlFor={inputId}
            className="flex flex-col items-center justify-center gap-3 p-8 cursor-pointer"
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              dragActive ? 'bg-[#0B0B0B] text-white' : 'bg-[#F7F7F7] text-[#bbb]'
            }`}>
              {isMember ? (
                <Camera size={22} className={dragActive ? 'text-white' : 'text-[#bbb]'} />
              ) : (
                <ImageIcon size={22} className={dragActive ? 'text-white' : 'text-[#bbb]'} />
              )}
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-[#0B0B0B] mb-1">
                {dragActive ? 'Lepaskan fail di sini' : 'Seret & lepas atau klik untuk pilih'}
              </p>
              <p className="text-xs text-[#999]">{ALLOWED_EXT} · Saiz maksimum {MAX_MB}MB</p>
              {hint && <p className="text-xs text-[#bbb] mt-1">{hint}</p>}
            </div>
            <div className="flex items-center gap-1.5 border border-[#E5E5E5] bg-[#F7F7F7] text-[#555] text-xs font-medium px-4 py-1.5 rounded">
              <Upload size={12} /> Pilih Fail
            </div>
          </label>
        </div>
      )}

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleChange}
        className="hidden"
        data-testid={`upload-input-${entityType}`}
      />

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-xs text-[#D71920]">
          <AlertCircle size={12} /> {error}
        </div>
      )}
    </div>
  );
};

export default UploadCard;
